import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { API_BASE_URL } from "../services/api";
import DSCRGauge from "../components/DSCRGauge";
import RecommendationPanel from "../components/RecommendationPanel";
import StrategyCard from "../components/StrategyCard";
import TrendCard from "../components/TrendCard";
import ForecastChart from "../components/ForecastChart";
import { saveAnalysis } from "../lib/analyses";
import { resolveRequestKey, isIdempotencyConflict, KEY_REUSED_MESSAGE } from "../lib/requestKey";
import OpportunitySignalCard from "../components/OpportunitySignalCard";
import { supabase } from "../lib/supabaseClient";
import { getLeaseTerms } from "../lib/leaseTerms";
import { getLeaseScenarios } from "../lib/leaseScenarios";
import { InfoTooltip, METRIC_TOOLTIPS } from "../components/InfoTooltip";
import { track } from "../lib/analytics";
import { firstPropertySearch, onboardingCompleted } from "../lib/onboardingFunnelAnalytics";
import { markOnce } from "../lib/onboardingFunnelState";

// =========================
// HELPERS
// =========================
function usd(n) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString()}`;
}
function pct(n) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}
function num(n, digits = 2) {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return n.toFixed(digits);
}

// =========================
// FORM VALIDATION
// Either the user picked an address (which auto-populates lat/lng) OR they
// entered lat/lng by hand. Either way, lat/lng must be valid finite numbers
// inside the legal range before we let them analyze.
// =========================
function isLatLngValid(form) {
  const lat = Number(form.latitude);
  const lng = Number(form.longitude);
  return (
    isFinite(lat) && lat !== 0 && Math.abs(lat) <= 90 &&
    isFinite(lng) && lng !== 0 && Math.abs(lng) <= 180
  );
}

// Coerce a value to a finite number, else return `fallback`. Used when
// re-hydrating numeric form fields from a saved analysis where the source
// may be a string, null, or undefined.
function numOr(v, fallback) {
  if (v === null || v === undefined || v === "") return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

// Build the form's initial state. If a saved analysis was passed in via
// React Router state (from /my-analyses → "Open in Deal Analyzer"), seed
// every field from it so the user lands on a re-runnable copy of the
// previous analysis. Otherwise fall back to the original defaults.
//
// Exported so unit tests (RunAnalysis.test.jsx) can verify the prefill
// shape without mounting the full DealAnalyzer component — pure function,
// no React hooks called.
export function buildInitialForm(prefill) {
  const defaults = {
    purchasePrice: 250000,
    loanAmount: 200000,
    interestRate: 6.5,
    latitude: "",
    longitude: "",
    forecastMonths: 3,
    runSimulation: true,
    address: "",
    formatted_address: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    place_id: "",
    // v1.0.0 Phase 3 / DEF-006 — populated when prefill comes from /leads.
    // entry_type + lead_id drive the lease-leverage overlay (STR NOI fetched
    // in a useEffect). currentMonthlyRevenue is an additive signal for the
    // recommendation engine to compute revenueUplift = projected − current.
    entry_type: null,
    lead_id: null,
    currentMonthlyRevenue: null,
  };

  if (!prefill) return defaults;

  const savedResult = prefill.result || {};
  const savedInput = savedResult.input || {};

  return {
    ...defaults,
    // Prefill order: lead asking_price (from /leads handoff) wins; falls
    // back to a saved analysis's purchasePrice (from /my-analyses re-open);
    // ultimately to the default. Same pattern as lat/lng below.
    purchasePrice: numOr(
      prefill.asking_price ?? savedInput.purchasePrice,
      defaults.purchasePrice
    ),
    loanAmount: numOr(savedInput.loanAmount, defaults.loanAmount),
    interestRate: numOr(savedInput.interestRate, defaults.interestRate),
    forecastMonths: numOr(savedInput.forecastMonths, defaults.forecastMonths),
    // Backend normalizes runSimulation → simulate; accept either on read.
    runSimulation:
      savedInput.simulate ??
      savedInput.runSimulation ??
      defaults.runSimulation,
    latitude: numOr(
      prefill.lat ?? savedInput.latitude ?? savedInput.lat,
      defaults.latitude
    ),
    longitude: numOr(
      prefill.lng ?? savedInput.longitude ?? savedInput.lng,
      defaults.longitude
    ),
    address:
      prefill.formatted_address ||
      (typeof savedInput.address === "string"
        ? savedInput.address
        : savedInput.address?.formatted_address || ""),
    formatted_address: prefill.formatted_address || "",
    street: prefill.street || "",
    city: prefill.city || "",
    state: prefill.state || "",
    zip: prefill.zip || "",
    place_id: prefill.place_id || "",
    entry_type: prefill.entry_type || null,
    lead_id:    prefill.lead_id    || null,
    currentMonthlyRevenue: numOr(
      prefill.current_monthly_revenue,
      defaults.currentMonthlyRevenue
    ),
  };
}

// Re-hydrate the saved ANALYSIS RESULT (not just the form inputs) when a
// saved analysis is re-opened from /my-analyses. The full analyze-property
// response was persisted verbatim to analyses.result (lib/analyses.js) and
// rides back in on location.state.prefill.result — byte-for-byte the same
// object setResult(data) consumes after a live run, so seeding `result` from
// it reproduces the saved dashboard exactly, with no re-run required.
//
// Returns the saved result ONLY when the prefill carries a real analysis
// (a MyAnalyses re-open). A bare lead handoff from /leads ("Run Analysis")
// has no result yet, and a fresh visit has no prefill — both return null so
// the user lands on the empty state and runs a fresh analysis. The hallmark
// check (engine outputs present) guards against rendering an empty dashboard
// from a malformed row that only echoes input.
//
// Exported so unit tests (SavedAnalysisRehydration.test.jsx) can verify the
// rehydration source without mounting the component — pure, no React hooks.
export function buildInitialResult(prefill) {
  if (!prefill || typeof prefill !== "object") return null;
  const saved = prefill.result;
  if (!saved || typeof saved !== "object") return null;
  const hasAnalysis =
    saved.recommendation != null ||
    saved.financials != null ||
    saved.dealScore != null ||
    saved.revenue != null ||
    saved.market != null;
  return hasAnalysis ? saved : null;
}

// Map Google's address_components blob into street/city/state/zip strings.
function flattenComponents(components = {}) {
  const street = [components.street_number, components.route]
    .filter(Boolean)
    .join(" ")
    .trim();
  const city =
    components.locality ||
    components.sublocality ||
    components.administrative_area_level_2 ||
    "";
  const state = components.administrative_area_level_1_short || "";
  const zip = components.postal_code || "";
  return { street, city, state, zip };
}

// Map a recommendation decision string to the design system badge class.
function badgeClassFor(decision) {
  const d = String(decision || "").toUpperCase();
  if (d === "BUY")   return "badge-buy";
  if (d === "HOLD")  return "badge-hold";
  if (d === "PASS")  return "badge-pass-rec";
  if (d === "LEASE") return "badge-lease";
  return "";
}

// =========================
// MAIN COMPONENT
// =========================
export default function DealAnalyzer() {
  // If we arrived here from /my-analyses → "Open in Deal Analyzer", the
  // saved analysis row rides in on location.state.prefill. buildInitialForm
  // seeds the form from it; on a normal fresh visit, prefill is undefined
  // and the original defaults apply (lat/lng EMPTY — user must enter an
  // address that auto-fills or supply lat/lng manually).
  const location = useLocation();
  const [form, setForm] = useState(() =>
    buildInitialForm(location.state?.prefill)
  );

  // v1.0.0 Phase 3 — lease-leverage overlay input. Fetched once on mount
  // when the prefill carries entry_type='lease_leverage' + a lead_id.
  // Stays null otherwise; the engine no-ops on Rule B when it's null/NaN.
  // Failure is silent — analysis still runs, just without the
  // catastrophic-floor PASS override.
  const [strScenarioMonthlyNoi, setStrScenarioMonthlyNoi] = useState(null);
  useEffect(() => {
    let cancelled = false;
    async function fetchStrNoi() {
      if (form.entry_type !== "lease_leverage" || !form.lead_id) return;
      const { data: termsData } = await getLeaseTerms(form.lead_id);
      if (cancelled || !termsData?.lease_terms) return;
      const { data: scenariosData } = await getLeaseScenarios(termsData.lease_terms.id);
      if (cancelled) return;
      const strRow = (scenariosData?.scenarios || []).find((s) => s.scenario_type === "str");
      if (strRow && strRow.monthly_noi !== null && strRow.monthly_noi !== undefined) {
        setStrScenarioMonthlyNoi(Number(strRow.monthly_noi));
      }
    }
    fetchStrNoi();
    return () => { cancelled = true; };
  }, [form.entry_type, form.lead_id]);

  function handleAddressSelect({ address, lat, lng, placeId, components }) {
    // Onboarding funnel — First Property Search (funnel step 6), once per session.
    if (typeof window !== "undefined" && markOnce(window.sessionStorage, "rentava_first_property_search")) {
      firstPropertySearch({ source: "address_select" });
    }
    const flat = flattenComponents(components);
    setForm((prev) => ({
      ...prev,
      address,
      formatted_address: address,
      latitude: Number(lat.toFixed(6)),
      longitude: Number(lng.toFixed(6)),
      place_id: placeId || "",
      street: flat.street,
      city: flat.city,
      state: flat.state,
      zip: flat.zip,
    }));
  }

  // Seed from a saved analysis on a /my-analyses re-open so the saved
  // dashboard renders immediately (no re-run). null on a fresh visit or a
  // bare lead handoff → empty state. A subsequent Analyze Property re-run
  // replaces this snapshot via setResult(data).
  const [result, setResult] = useState(() =>
    buildInitialResult(location.state?.prefill)
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // BL-048 (ADR-068) — client request-idempotency key. Reused across a retry of
  // the SAME unchanged request (dedups server-side); regenerated when an
  // accepted field changes or on an explicit re-submit after a 409. `forceNew`
  // is set after a 409 so the next explicit Analyze mints a fresh key.
  const requestKeyState = useRef(null);
  const forceNewKeyRef = useRef(false);

  // Zip is auto-populated by autocomplete pick + reverse-geocode-on-blur.
  // We surface it read-only so the user can see the value the comp pipeline
  // will key on, and offer an explicit override toggle for edge cases (PO box
  // zips, multi-zip parcels) where the derived value isn't what they want.
  const [zipOverrideMode, setZipOverrideMode] = useState(false);

  // DEF-009 — failure hint for the ZIP-centroid geocode below. Empty string
  // means "no error to show". Cleared every time we start a new lookup.
  const [zipGeocodeError, setZipGeocodeError] = useState("");

  // Same dedupe-by-ref pattern as the lat/lng reverse geocode below — if
  // the user blurs the ZIP override field repeatedly with the same value,
  // we only hit the geocode endpoint once.
  const lastZipGeocodedRef = useRef("");

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? value === "" ? "" : Number(value)
          : value,
    }));
  }

  // When the user types lat/lng manually (no autocomplete pick), the form
  // is missing zip — which forces resolveComps to fall back to static comps.
  // On blur, call the reverse-geocode endpoint to derive zip/city/state from
  // the coordinates and silently fill the form. Skipped when:
  //   - lat/lng aren't both valid finite + in range
  //   - the user already selected a place via autocomplete (place_id set)
  //   - we've already resolved this exact lat/lng (dedupe by ref)
  const lastReverseGeocodeRef = useRef("");
  async function handleLatLngBlur() {
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (!isFinite(lat) || !isFinite(lng)) return;
    if (lat === 0 || lng === 0) return;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
    if (form.place_id) return;

    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (lastReverseGeocodeRef.current === key) return;
    lastReverseGeocodeRef.current = key;

    try {
      const res = await fetch(
        `${API_BASE_URL}/geocode-place?latlng=${encodeURIComponent(key)}`
      );
      const data = await res.json();
      if (!data?.success) return;

      const flat = flattenComponents(data.components || {});
      setForm((prev) => ({
        ...prev,
        zip:               prev.zip               || flat.zip,
        city:              prev.city              || flat.city,
        state:             prev.state             || flat.state,
        street:            prev.street            || flat.street,
        formatted_address: prev.formatted_address || data.formattedAddress || "",
        address:           prev.address           || data.formattedAddress || "",
        // Intentionally NOT setting place_id — leaving it empty preserves the
        // signal that this address was derived from manual lat/lng, not picked
        // from autocomplete. That keeps re-runs idempotent.
      }));
    } catch {
      // Network/API failure: stay silent. Analysis still runs on lat/lng,
      // just without the live-comp boost.
    }
  }

  // DEF-009 — when the user types a ZIP in override mode and Places
  // autocomplete never resolved (so lat/lng are still empty), forward
  // the ZIP to Google's Geocoding API and populate lat/lng with the ZIP
  // centroid. This unblocks Analyze Property for unaddressed parcels.
  // Skipped when:
  //   - override mode is off (read-only field never fires onBlur from user)
  //   - ZIP is empty
  //   - EITHER lat or lng is already populated (don't overwrite a
  //     half-filled manual entry — only fires when both are blank)
  //   - we've already geocoded this exact ZIP value (dedupe by ref)
  async function handleZipBlur() {
    if (!zipOverrideMode) return;
    const zip = (form.zip || "").trim();
    if (!zip) return;
    const latEmpty = form.latitude  === "" || form.latitude  == null;
    const lngEmpty = form.longitude === "" || form.longitude == null;
    if (!latEmpty || !lngEmpty) return;
    if (lastZipGeocodedRef.current === zip) return;
    lastZipGeocodedRef.current = zip;
    setZipGeocodeError("");

    try {
      const res = await fetch(
        `${API_BASE_URL}/geocode-place?address=${encodeURIComponent(`${zip} USA`)}`
      );
      const data = await res.json();
      if (
        !data?.success ||
        typeof data.lat !== "number" ||
        typeof data.lng !== "number"
      ) {
        setZipGeocodeError("Could not locate ZIP — enter lat/lng manually.");
        return;
      }
      setForm((prev) => ({
        ...prev,
        latitude:  Number(data.lat.toFixed(6)),
        longitude: Number(data.lng.toFixed(6)),
      }));
    } catch {
      setZipGeocodeError("Could not locate ZIP — enter lat/lng manually.");
    }
  }

  const canAnalyze = isLatLngValid(form) && !loading;
  const validationHint = !isLatLngValid(form)
    ? "Enter an address above (or fill in latitude + longitude) to analyze."
    : "";

  async function handleAnalyze() {
    if (!isLatLngValid(form)) {
      setError("Address or latitude/longitude is required to analyze.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    track('analysis_run', {
      zip:         form.zip || null,
      entry_type:  form.entry_type || null,
      has_lead_id: !!form.lead_id,
    });

    try {
      const payload = {
        purchasePrice: Number(form.purchasePrice),
        loanAmount: Number(form.loanAmount),
        interestRate: Number(form.interestRate),
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        forecastMonths: Number(form.forecastMonths),
        runSimulation: form.runSimulation,

        // Address context — only included when present
        address: form.address || form.formatted_address || "",
        formatted_address: form.formatted_address || null,
        street: form.street || null,
        city: form.city || null,
        state: form.state || null,
        zip: form.zip || null,
        place_id: form.place_id || null,

        // v1.0.0 Phase 3 — lease-leverage overlay. Engine no-ops on both
        // unless entry_type === 'lease_leverage'. strScenarioMonthlyNoi
        // null means "we don't have STR data" — Rule A may still relabel
        // BUY → LEASE, but the catastrophic Rule B PASS check is skipped.
        entry_type: form.entry_type || null,
        strScenarioMonthlyNoi:
          strScenarioMonthlyNoi !== null && Number.isFinite(strScenarioMonthlyNoi)
            ? strScenarioMonthlyNoi
            : null,

        // DEF-006 — additive signal: the lot's currently-observed monthly
        // revenue (when the lead carried it through, or the user typed it
        // here). The engine computes revenueUplift = projected − current and
        // uses it to nudge the recommendation confidence, never the label.
        current_monthly_revenue: form.currentMonthlyRevenue
          ? Number(form.currentMonthlyRevenue) : null,

        // BL-048 (ADR-068) — lead_id is now an accepted, fingerprinted request
        // field (a changed lead_id → new key → server 409 on the old key).
        lead_id: form.lead_id || null,
      };

      // BL-048 — bind a request_key: reuse for an unchanged retry, regenerate
      // on any accepted-field change or an explicit re-submit after a 409.
      const resolvedKey = resolveRequestKey(requestKeyState.current, payload, { forceNew: forceNewKeyRef.current });
      requestKeyState.current = { key: resolvedKey.key, fingerprint: resolvedKey.fingerprint };
      forceNewKeyRef.current = false;
      payload.request_key = resolvedKey.key;

      // BL-029A (ADR-037): analyze-property is now Authenticated + Tier.
      // Send the signed-in user's Bearer token (the backend derives identity
      // + entitlement from it; the paywall is enforced server-side too) and
      // use the configured API base instead of a hardcoded localhost URL.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error("Your session has expired. Please sign in again.");
      }

      const response = await fetch(
        `${API_BASE_URL}/analyze-property`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const text = await response.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Backend returned invalid JSON");
      }

      // BL-048 (ADR-068) — 409 idempotency_key_reused: STOP, show a bounded
      // message, DO NOT auto-resubmit, preserve form state. The next explicit
      // Analyze mints a fresh key (forceNew).
      if (isIdempotencyConflict(response, data)) {
        forceNewKeyRef.current = true;
        setError(KEY_REUSED_MESSAGE);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || "Analysis failed");
      }

      setResult(data);
      track('analysis_completed', {
        zip:            form.zip || null,
        deal_score:     data?.dealScore ?? null,
        recommendation: data?.recommendation ?? null,
        dscr:           data?.dscr ?? null,
        entry_type:     form.entry_type || null,
      });
      // Onboarding funnel — Onboarding Completed (funnel step 11, terminal
      // activation milestone), fired once per user at first analysis completion.
      const funnelUid = sessionData?.session?.user?.id;
      if (funnelUid && typeof window !== "undefined" &&
          markOnce(window.localStorage, `rentava_onboarding_completed_${funnelUid}`)) {
        onboardingCompleted({ milestone: 'first_analysis_completed' });
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Analysis failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        background: "var(--color-navy)",
        minHeight: "100%",
        padding: "var(--space-6)",
        color: "var(--color-white)",
        fontFamily: "var(--font-body)",
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* ── Page header ───────────────────────────────────── */}
        <header style={{ marginBottom: "var(--space-6)" }}>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-4xl)",
              fontWeight: "var(--weight-black)",
              letterSpacing: "var(--tracking-tight)",
              textTransform: "uppercase",
              color: "var(--color-white)",
              margin: 0,
              marginBottom: "var(--space-2)",
            }}
          >
            Deal Analyzer
          </h1>
          <p
            style={{
              fontSize: "var(--text-md)",
              lineHeight: "var(--leading-normal)",
              color: "var(--color-steel-lt)",
              margin: 0,
            }}
          >
            Live opportunity intelligence with comp analysis, forecasting,
            DSCR analysis, market simulation, and investor strategy ranking.
          </p>
        </header>

        {/* ── Input panel ───────────────────────────────────── */}
        <section className="card" style={{ marginBottom: "var(--space-6)" }}>
          {/* Address autocomplete — populates latitude/longitude on select. */}
          <div style={{ marginBottom: "var(--space-4)" }}>
            <AddressAutocomplete
              onSelect={handleAddressSelect}
              initialValue={form.address}
            />
          </div>

          {/* Derived zip — visible so the user can see / override which zip
              the comp pipeline will key on. Auto-fills from autocomplete or
              reverse-geocode; manual edits stick once Override is toggled. */}
          <ZipField
            value={form.zip}
            editable={zipOverrideMode}
            onChange={handleChange}
            onToggleEdit={() => setZipOverrideMode((m) => !m)}
            onZipBlur={handleZipBlur}
            zipGeocodeError={zipGeocodeError}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "var(--space-4)",
              marginBottom: "var(--space-5)",
            }}
          >
            <Input label="Purchase Price"   name="purchasePrice" value={form.purchasePrice} onChange={handleChange} />
            <Input label="Loan Amount"      name="loanAmount"    value={form.loanAmount}    onChange={handleChange} />
            <Input label="Interest Rate %"  name="interestRate"  value={form.interestRate}  onChange={handleChange} />
            <Input label="Latitude"         name="latitude"      value={form.latitude}      onChange={handleChange} onBlur={handleLatLngBlur} />
            <Input label="Longitude"        name="longitude"     value={form.longitude}     onChange={handleChange} onBlur={handleLatLngBlur} />
            <Input
              label="Current Monthly Revenue (if occupied)"
              name="currentMonthlyRevenue"
              value={form.currentMonthlyRevenue ?? ""}
              onChange={handleChange}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "var(--space-4)",
              alignItems: "end",
            }}
          >
            <div className="input-group">
              <label className="input-label" htmlFor="forecastMonths">
                Forecast Months
              </label>
              <input
                id="forecastMonths"
                type="number"
                name="forecastMonths"
                className="input"
                value={form.forecastMonths}
                onChange={handleChange}
              />
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-2)",
                color: "var(--color-white-dim)",
                fontSize: "var(--text-base)",
                fontWeight: "var(--weight-medium)",
              }}
            >
              <input
                type="checkbox"
                name="runSimulation"
                checked={form.runSimulation}
                onChange={handleChange}
              />
              Run market simulation
            </label>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "var(--space-2)" }}>
              <button
                type="button"
                onClick={handleAnalyze}
                disabled={!canAnalyze}
                title={validationHint || "Run the analyzer"}
                className="btn btn-primary btn-lg"
              >
                {loading ? "Analyzing…" : "Analyze Property"}
              </button>
              {validationHint && (
                <span className="input-hint">{validationHint}</span>
              )}
            </div>
          </div>
        </section>

        {/* ── Error ─────────────────────────────────────────── */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: "var(--space-6)",
              padding: "var(--space-4) var(--space-6)",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--color-danger)",
              background: "var(--color-danger-ghost)",
              color: "var(--color-danger-lt)",
              fontWeight: "var(--weight-semibold)",
              fontSize: "var(--text-base)",
            }}
          >
            {error}
          </div>
        )}

        {/* ── Results or empty state ───────────────────────── */}
        {result
          ? <ResultsDashboard result={result} input={form} />
          : <EmptyState />
        }
      </div>
    </div>
  );
}

// =========================
// EMPTY STATE
// =========================
function EmptyState() {
  return (
    <div
      className="card"
      style={{
        textAlign: "center",
        padding: "var(--space-12) var(--space-6)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "var(--text-2xl)",
          fontWeight: "var(--weight-bold)",
          color: "var(--color-white)",
          marginBottom: "var(--space-2)",
        }}
      >
        Ready when you are
      </div>
      <p
        style={{
          fontSize: "var(--text-base)",
          color: "var(--color-steel-lt)",
          maxWidth: 480,
          margin: "0 auto",
          lineHeight: "var(--leading-normal)",
        }}
      >
        Enter a property address (or lat/lng) above and click{" "}
        <strong style={{ color: "var(--color-amber)" }}>Analyze Property</strong>{" "}
        to see your opportunity signal, deal score, DSCR, comps, and scenarios.
      </p>
    </div>
  );
}

// =========================
// RESULTS DASHBOARD
// =========================
function ResultsDashboard({ result, input }) {
  const dscr = result.financials?.dscr ?? 0;
  const monthlyRevenue = result.revenue?.monthlyGross;
  const noi = result.financials?.noi;
  const dealScore = result.dealScore;
  const decision = result.recommendation?.decision;

  return (
    <div style={{ display: "grid", gap: "var(--space-6)" }}>
      {/* ── Recommendation headline — the answer ────────────── */}
      <RecommendationHeadline
        decision={decision}
        dealScore={dealScore}
        input={input}
        result={result}
      />

      {/* ── KPI strip ───────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "var(--space-4)",
        }}
      >
        <StatCard label="DSCR"                       value={num(dscr)}           tooltip={METRIC_TOOLTIPS.dscr} />
        <StatCard label="NOI"                        value={usd(noi)}            tooltip={METRIC_TOOLTIPS.noi} />
        <StatCard label="Projected Monthly Revenue"  value={usd(monthlyRevenue)} tooltip={METRIC_TOOLTIPS.projectedMonthlyRevenue} />
        <StatCard label="Deal Score"                 value={dealScore ?? "—"}    tooltip={METRIC_TOOLTIPS.dealScore} />
      </div>

      {/* ── DSCR gauge + Recommendation Panel ───────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "var(--space-6)",
        }}
      >
        <div className="card">
          <RecommendationPanel
            recommendation={result.recommendation}
            forecast={result.forecast}
          />
        </div>
        <div className="card">
          <DSCRGauge value={dscr} />
        </div>
      </div>

      {/* ── Forecast chart ──────────────────────────────────── */}
      <div className="card">
        <ForecastChart forecast={result.forecast} />
      </div>

      {/* ── Trend + Strategy ────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "var(--space-6)",
        }}
      >
        <div className="card">
          <TrendCard trends={result.trends} marketScore={result.marketScore} />
        </div>
        <div className="card">
          <StrategyCard strategy={result.strategy} />
        </div>
      </div>

      {/* ── Scenario comparison ─────────────────────────────── */}
      <ScenarioComparison scenarios={result.scenarios} />

      {/* ── Comps + subject footer ──────────────────────────── */}
      <CompsFooter result={result} />
    </div>
  );
}

// =========================
// RECOMMENDATION HEADLINE
// Big visible "BUY / HOLD / PASS / LEASE" badge + Deal Score + Save CTA.
// Always at the top of results so the answer is immediate.
// =========================
function RecommendationHeadline({ decision, dealScore, input, result }) {
  const badgeClass = badgeClassFor(decision);
  return (
    <div
      className="card-accent"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "var(--space-5)",
        alignItems: "center",
      }}
    >
      <div>
        <SectionLabel>Opportunity Signal</SectionLabel>
        <div style={{ marginTop: "var(--space-3)" }}>
          <span
            className={`badge ${badgeClass}`}
            style={{
              fontSize: "var(--text-lg)",
              padding: "var(--space-2) var(--space-5)",
            }}
          >
            {decision || "—"}
          </span>
        </div>
      </div>

      <div>
        <SectionLabel>Deal Score</SectionLabel>
        <div
          style={{
            marginTop: "var(--space-3)",
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-5xl)",
            fontWeight: "var(--weight-black)",
            color: "var(--color-amber)",
            lineHeight: "var(--leading-none)",
          }}
        >
          {dealScore ?? "—"}
        </div>
      </div>

      <OpportunitySignalCard result={result} />
      <SaveAnalysisAction input={input} result={result} leadId={input?.lead_id ?? null} />
    </div>
  );
}

// =========================
// STAT CARD — DESIGN SYSTEM .card-stat
// =========================
function StatCard({ label, value, tooltip }) {
  return (
    <div className="card-stat">
      <div
        className="card-stat-label"
        style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)" }}
      >
        <span>{label}</span>
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className="card-stat-value">{value}</div>
    </div>
  );
}

// =========================
// SECTION LABEL — small caps eyebrow above a value
// =========================
function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: "var(--text-xs)",
        fontWeight: "var(--weight-bold)",
        letterSpacing: "var(--tracking-label)",
        textTransform: "uppercase",
        color: "var(--color-muted)",
      }}
    >
      {children}
    </div>
  );
}

// =========================
// COMPS FOOTER — subject address + data sources, scannable
// =========================
function CompsFooter({ result }) {
  // DEF-011 — subjectAddress is now a string (see analyzeProperty.js); keep
  // a defensive coercion on the fallback so a stale-shaped result (object)
  // can never reach JSX and crash React.
  const subjectAddress =
    result.subjectAddress ||
    (typeof result.input?.address === "string"
      ? result.input.address
      : result.input?.address?.formatted_address || null);
  const lat = result.input?.latitude ?? result.input?.lat;
  const lng = result.input?.longitude ?? result.input?.lng;

  return (
    <div className="card">
      <SectionLabel>Comps &amp; Subject</SectionLabel>
      <div
        style={{
          marginTop: "var(--space-3)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        {subjectAddress && (
          <div style={{ fontSize: "var(--text-base)", color: "var(--color-white)" }}>
            <span style={{ color: "var(--color-steel)" }}>Subject property: </span>
            <strong>{subjectAddress}</strong>
            {(lat || lng) && (
              <span style={{ color: "var(--color-steel)", marginLeft: "var(--space-2)" }}>
                ({Number(lat).toFixed(5)}, {Number(lng).toFixed(5)})
              </span>
            )}
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "var(--space-3)",
            alignItems: "center",
            fontSize: "var(--text-base)",
            color: "var(--color-white-dim)",
          }}
        >
          <span>
            Comps used: <strong style={{ color: "var(--color-white)" }}>{result.compsCount ?? "—"}</strong>
          </span>
          <CompSourceBadge dataSources={result.dataSources} />
        </div>
      </div>
    </div>
  );
}

// =========================
// COMP SOURCE BADGE (v0.9.0)
// Shows whether the analysis used live ingested comps or static fallback.
// =========================
export function CompSourceBadge({ dataSources }) {
  if (!dataSources) return null;

  const source     = dataSources.compsSource;   // 'live' | 'fallback_ingesting' | 'fallback_no_zip'
  const oldestComp = dataSources.oldestComp;
  const sources    = dataSources.sources || [];

  let ageLabel = null;
  if (oldestComp) {
    const days = Math.round((Date.now() - new Date(oldestComp).getTime()) / 86400000);
    ageLabel = days === 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`;
  }

  const isLive       = source === "live";
  const isRefreshing = source === "fallback_ingesting";

  const badgeClass = isLive
    ? "badge-active"
    : isRefreshing
    ? "badge-reviewing"
    : "badge";

  // W3 Step 6 — premium licensed sources (Mashvisor + RentCast) surface
  // by name in the LIVE label; generic / unvalidated sources (airbnb,
  // airdna) stay anonymous per the original "Rentava's intelligence is
  // the story" policy. Fallback + refreshing states unchanged.
  const PREMIUM_LABELS = {
    mashvisor: "MASHVISOR",
    rentcast:  "RENTCAST",
  };
  const premiumNames = sources
    .map((s) => PREMIUM_LABELS[s])
    .filter(Boolean);
  const sourceSegment = premiumNames.length > 0
    ? premiumNames.join(" + ")
    : "MULTI-SOURCE";

  const label = isLive
    ? `LIVE COMPS · ${sourceSegment}${ageLabel ? ` · UPDATED ${ageLabel.toUpperCase()}` : ""}`
    : isRefreshing
    ? "REFRESHING COMPS..."
    : "FALLBACK COMPS · ESTIMATED DATA";

  return <span className={`badge ${badgeClass}`}>{label}</span>;
}

// =========================
// SCENARIO COMPARISON
// =========================
function ScenarioComparison({ scenarios }) {
  if (!scenarios) return null;

  const items = [
    { key: "base",       label: "Base",       tone: "var(--color-steel)" },
    { key: "optimized",  label: "Optimized",  tone: "var(--color-amber)" },
    { key: "aggressive", label: "Aggressive", tone: "var(--color-lease-lt)" },
  ].map((meta) => ({ ...meta, data: scenarios[meta.key] }))
   .filter((s) => s.data);

  if (items.length === 0) return null;

  const target = scenarios.targetAnalysis;

  return (
    <div className="card">
      <SectionLabel>Scenario Comparison</SectionLabel>

      <div
        style={{
          marginTop: "var(--space-4)",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "var(--space-4)",
        }}
      >
        {items.map((s) => (
          <div
            key={s.key}
            style={{
              border: "1px solid var(--color-navy-border)",
              borderTop: `3px solid ${s.tone}`,
              borderRadius: "var(--radius-md)",
              padding: "var(--space-4)",
              background: "var(--color-navy)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: "var(--weight-bold)",
                fontSize: "var(--text-lg)",
                color: s.tone,
                marginBottom: "var(--space-3)",
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-wide)",
              }}
            >
              {s.label}
            </div>
            <ScenarioRow label="Nightly rate"  value={usd(s.data.nightlyRate)} />
            <ScenarioRow label="Occupancy"     value={pct(s.data.occupancy)} />
            <ScenarioRow label="Monthly rev"   value={usd(s.data.revenue?.monthlyGross)} />
            <ScenarioRow label="NOI"           value={usd(s.data.financials?.noi)} />
            <ScenarioRow label="Debt service"  value={usd(s.data.financials?.debtService)} />
            <ScenarioRow label="DSCR"          value={num(s.data.dscr)} bold />
          </div>
        ))}
      </div>

      {target && (
        <div
          style={{
            marginTop: "var(--space-4)",
            padding: "var(--space-3) var(--space-4)",
            background: "var(--color-navy)",
            border: "1px solid var(--color-navy-border)",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--text-base)",
            color: "var(--color-white-dim)",
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--space-4)",
          }}
        >
          <span>Target DSCR: <strong style={{ color: "var(--color-white)" }}>{num(target.targetDSCR)}</strong></span>
          <span>Current DSCR: <strong style={{ color: "var(--color-white)" }}>{num(target.currentDSCR)}</strong></span>
          {typeof target.requiredNOI === "number" && (
            <span>Required NOI: <strong style={{ color: "var(--color-white)" }}>{usd(target.requiredNOI)}</strong></span>
          )}
          {typeof target.monthlyGap === "number" && (
            <span style={{ color: target.monthlyGap <= 0 ? "var(--color-success-lt)" : "var(--color-danger-lt)" }}>
              {target.monthlyGap <= 0
                ? <>Monthly surplus: <strong>{usd(Math.abs(target.monthlyGap))}</strong> above target</>
                : <>Monthly gap: <strong>{usd(target.monthlyGap)}</strong> needed</>
              }
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ScenarioRow({ label, value, bold }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "var(--space-2) 0",
        borderBottom: "1px dashed var(--color-navy-border)",
        fontSize: "var(--text-base)",
      }}
    >
      <span style={{ color: "var(--color-steel)" }}>{label}</span>
      <span
        style={{
          color: "var(--color-white)",
          fontWeight: bold ? "var(--weight-black)" : "var(--weight-semibold)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// =========================
// ZIP FIELD — labeled, override toggle
// =========================
function ZipField({ value, editable, onChange, onToggleEdit, onZipBlur, zipGeocodeError }) {
  const hasValue = !!(value && String(value).trim());
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--space-3)",
        marginBottom: "var(--space-5)",
        padding: "var(--space-3) var(--space-4)",
        background: "var(--color-navy)",
        border: "1px solid var(--color-navy-border)",
        borderRadius: "var(--radius-md)",
      }}
    >
      <label htmlFor="deal-analyzer-zip" className="input-label">
        ZIP
      </label>
      <input
        id="deal-analyzer-zip"
        type="text"
        name="zip"
        inputMode="numeric"
        autoComplete="postal-code"
        value={value || ""}
        readOnly={!editable}
        onChange={onChange}
        onBlur={onZipBlur}
        placeholder="Auto-derived from address or coordinates"
        className="input"
        style={{ width: 180, flexShrink: 0 }}
      />
      <button
        type="button"
        onClick={onToggleEdit}
        className="btn btn-ghost btn-sm"
      >
        {editable ? "Done" : "Override"}
      </button>
      {zipGeocodeError ? (
        <span
          className="input-error-msg"
          style={{ flex: 1, minWidth: 200 }}
        >
          {zipGeocodeError}
        </span>
      ) : (
        <span className="input-hint" style={{ flex: 1, minWidth: 200 }}>
          {editable
            ? "Editing — analysis will use this zip for comp lookup."
            : hasValue
            ? "Auto-populated. Click Override to edit."
            : "Pick an address or enter lat/lng to auto-derive."}
        </span>
      )}
    </div>
  );
}

// =========================
// FORM INPUT — labeled numeric input using design system .input
// =========================
function Input({ label, name, value, onChange, onBlur }) {
  return (
    <div className="input-group">
      <label htmlFor={name} className="input-label">{label}</label>
      <input
        id={name}
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className="input"
      />
    </div>
  );
}

// =========================
// SAVE ANALYSIS ACTION
// Persists the current analysis to the `analyses` table (RLS scopes rows
// to the signed-in user). State machine: idle → saving → saved | error.
// Resets when a new `result` arrives so a re-run = a fresh save opportunity.
// =========================
function SaveAnalysisAction({ input, result, leadId }) {
  const [state, setState] = useState("idle"); // 'idle' | 'saving' | 'saved' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setState("idle");
    setErrorMsg("");
  }, [result]);

  // BL-048 (ADR-068) — when the server persisted this analysis on the
  // authoritative path (response carries analysis_id), the client SKIPS
  // saveAnalysis entirely: it is already retained. No "Save to track" second
  // decision — just confirm + deep-link to the stable route.
  if (result?.analysis_id) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", alignItems: "flex-start" }}>
        <SectionLabel>Saved</SectionLabel>
        <span className="badge badge-active" data-testid="auto-saved">Saved ✓</span>
        <Link
          to={`/my-analyses/${result.analysis_id}`}
          style={{ color: "var(--color-amber)", textDecoration: "none", fontWeight: "var(--weight-semibold)", fontSize: "var(--text-base)" }}
        >
          Open analysis →
        </Link>
      </div>
    );
  }

  async function handleSave() {
    setState("saving");
    setErrorMsg("");
    // DEF-014 — pass leadId so saveAnalysis can mirror into lead_analyses
    // and the parent lead's saved-analyses list (DEF-015) picks it up.
    const { error } = await saveAnalysis(input, result, { leadId });
    if (error) {
      setState("error");
      setErrorMsg(error.message || "Could not save analysis.");
      return;
    }
    setState("saved");
    track('analysis_saved', {
      zip:            input?.zip || null,
      deal_score:     result?.dealScore ?? null,
      recommendation: result?.recommendation ?? null,
      dscr:           result?.dscr ?? null,
      lead_id:        leadId ?? null,
    });
  }

  if (state === "saved") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", alignItems: "flex-start" }}>
        <SectionLabel>Saved</SectionLabel>
        <span className="badge badge-active">Saved ✓</span>
        <Link
          to="/my-analyses"
          style={{
            color: "var(--color-amber)",
            textDecoration: "none",
            fontWeight: "var(--weight-semibold)",
            fontSize: "var(--text-base)",
          }}
        >
          View saved analyses →
        </Link>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", alignItems: "flex-start" }}>
      <SectionLabel>Save</SectionLabel>
      <button
        type="button"
        onClick={handleSave}
        disabled={state === "saving"}
        className="btn btn-primary"
      >
        {state === "saving" ? "Saving…" : "Save analysis"}
      </button>
      {state === "error" && (
        <span className="input-error-msg">{errorMsg}</span>
      )}
    </div>
  );
}
